import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  Truck, 
  Ship, 
  Plane, 
  Warehouse, 
  Package, 
  Globe, 
  Shield, 
  Clock,
  Users,
  BarChart3,
  FileText,
  Settings,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
  CheckCircle,
  Droplets,
  Key,
  ChefHat,
  Building2,
  ChevronDown,
  ChevronRight,
  Fish,
  Wheat
} from 'lucide-react';


  const Services = () => {
  const [activeTab, setActiveTab] = useState('logistics');
  const [expandedCategories, setExpandedCategories] = useState({});
  const location = useLocation();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "LST OIL SA - Services Internationaux",
    "description": "Services complets en logistique internationale, hydrocarbures et produits alimentaires pour marchés mondiaux",
    "provider": {
      "@type": "Organization",
      "name": "LST OIL SA",
      "url": "https://www.lstoilsa.com"
    },
    "areaServed": ["World", "Africa", "Europe", "Asia", "Middle East"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services LST OIL SA",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Logistique Internationale" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Hydrocarbures et Énergie" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Produits Alimentaires Mondiaux" } }
      ]
    }
  };


  // Fonction améliorée pour gérer la navigation par hash
  const handleHashNavigation = (hash) => {
    if (!hash) return;

    let tabToActivate = activeTab;
    let categoryToExpand = null;

    // Mapping des hash vers les onglets et sous-catégories
    if (hash === 'logistique' || hash === 'transport' || hash === 'stockage' || hash === 'douanes') {
      tabToActivate = 'logistics';
      if (hash === 'transport') categoryToExpand = 'transport';
      if (hash === 'douanes') categoryToExpand = 'douanes';
      if (hash === 'stockage') categoryToExpand = 'stockage';
      if (hash === 'logistique') {
        // Pour "Logistique" général, ouvrir la première sous-catégorie
        categoryToExpand = 'transport';
      }
    }
    if (hash === 'bases' || hash === 'petrole' || hash === 'btp') {
      tabToActivate = 'bases';
      if (hash === 'petrole') categoryToExpand = 'petrole';
      if (hash === 'btp') categoryToExpand = 'btp';
      if (hash === 'bases') {
        // Pour "Secteurs Clés" général, ouvrir la première sous-catégorie
        categoryToExpand = 'petrole';
      }
    }
    if (hash === 'alimentaires' || hash === 'denrees' || hash === 'halieutiques') {
      tabToActivate = 'alimentaires';
      if (hash === 'denrees') categoryToExpand = 'denrees';
      if (hash === 'halieutiques') categoryToExpand = 'halieutiques';
      if (hash === 'alimentaires') {
        // Pour "Produits Alimentaires" général, ouvrir la première sous-catégorie
        categoryToExpand = 'denrees';
      }
    }

    // Changement d'onglet si nécessaire
    if (tabToActivate !== activeTab) {
      setActiveTab(tabToActivate);
      
      // Attendre que l'onglet soit changé avant de gérer l'expansion et le scroll
      setTimeout(() => {
        if (categoryToExpand) {
          setExpandedCategories(prev => ({
            [categoryToExpand]: true // Fermer les autres, ouvrir seulement celle demandée
          }));
        }
        
        // Scroll vers l'élément après un délai supplémentaire
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) {
            const yOffset = -100; // Compensation pour le header fixe
            const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }, 400);
      }, 200);
    } else {
      // Même onglet, gérer directement l'expansion et le scroll
      if (categoryToExpand) {
        setExpandedCategories(prev => ({
          [categoryToExpand]: true // Fermer les autres, ouvrir seulement celle demandée
        }));
      }
      
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) {
          const yOffset = -100; // Compensation pour le header fixe
          const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 400);
    }
  };

  useEffect(() => {
    const hash = location.hash?.replace('#', '');
    if (hash) {
      handleHashNavigation(hash);
    }
  }, [location.hash]);

  // Réinitialiser l'état de navigation quand on change d'onglet manuellement
  useEffect(() => {
    // Fermer toutes les catégories expandées quand on change d'onglet
    setExpandedCategories({});
  }, [activeTab]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const serviceCategories = [
    {
      id: 'logistics',
      title: 'Logistique',
      icon: Package,
      description: 'Solutions logistiques complètes incluant transport, douanes et stockage',
      color: 'from-blue-500 to-blue-600',
      hasSubcategories: true,
      subcategories: ['Transport', 'Douanes', 'Stockage']
    },
    {
      id: 'bases',
      title: 'Hydrocarbures & BTP',
      icon: Key,
      description: 'Expertise dans les Hydrocarbures et BTP',
      color: 'from-red-500 to-red-600',
      hasSubcategories: true,
      subcategories: ['Produits Pétroliers', 'BTP & Travaux Publics']
    },
    {
      id: 'alimentaires',
      title: 'Produits Alimentaires',
      icon: ChefHat,
      description: 'Solutions complètes pour denrées et produits halieutiques',
      color: 'from-green-500 to-green-600',
      hasSubcategories: true,
      subcategories: ['Denrées Première Nécessité', 'Produits Halieutiques']
    }
  ];

  const services = {
    logistics: {
      transport: [
        {
          title: 'Transport Routier',
          description: 'Transport routier national et international avec flotte moderne et sécurisée',
          features: ['Flotte moderne', 'Chauffeurs qualifiés', 'Suivi GPS', 'Assurance complète'],
          icon: Truck
        },
        {
          title: 'Transport Maritime',
          description: 'Solutions de transport maritime pour vos marchandises internationales',
          features: ['Conteneurs', 'Vrac', 'Roulier', 'Suivi maritime'],
          icon: Ship
        },
        {
          title: 'Transport Aérien',
          description: 'Transport aérien express pour vos envois urgents et précieux',
          features: ['Fret express', 'Charter', 'Cargo régulier', 'Suivi en temps réel'],
          icon: Plane
        }
      ],
      douanes: [
        {
          title: 'Dédouanement',
          description: 'Services de dédouanement complets pour tous types de marchandises',
          features: ['Déclaration en douane', 'Calcul des droits', 'Procédures spéciales', 'Suivi des dossiers'],
          icon: FileText
        },
        {
          title: 'Transit International',
          description: 'Gestion des procédures de transit et transit international',
          features: ['Transit simple', 'Transit communautaire', 'Carnets TIR', 'Accompagnement'],
          icon: Globe
        },
        {
          title: 'Conformité Douanière',
          description: 'Conseil en conformité douanière et audit des procédures',
          features: ['Audit douanier', 'Formation équipes', 'Veille réglementaire', 'Gestion des risques'],
          icon: Shield
        }
      ],
      stockage: [
        {
          title: 'Entrepôts Sécurisés',
          description: 'Entrepôts modernes et sécurisés pour tous types de marchandises',
          features: ['Sécurité 24/7', 'Contrôle d\'accès', 'Système d\'alarme', 'Surveillance vidéo'],
          icon: Warehouse
        },
        {
          title: 'Gestion des Stocks',
          description: 'Gestion intelligente des stocks avec systèmes informatiques avancés',
          features: ['Inventaire permanent', 'Gestion des emplacements', 'Traçabilité', 'Rapports automatisés'],
          icon: BarChart3
        },
        {
          title: 'Services de Valeur Ajoutée',
          description: 'Services d\'emballage, étiquetage et préparation des commandes',
          features: ['Emballage sur mesure', 'Étiquetage', 'Préparation commandes', 'Personnalisation'],
          icon: Settings
        }
      ]
    },
    bases: {
      petrole: [
        {
          title: 'Distribution Pétrolière',
          description: 'Distribution et commerce de produits pétroliers et hydrocarbures de qualité',
          features: ['Carburants', 'Lubrifiants', 'Gaz industriels', 'Livraison sécurisée'],
          icon: Droplets
        },
        {
          title: 'Stockage Hydrocarbures',
          description: 'Solutions de stockage spécialisées pour produits pétroliers',
          features: ['Cuves sécurisées', 'Normes environnementales', 'Surveillance continue', 'Maintenance'],
          icon: Warehouse
        }
      ],
      btp: [
        {
          title: 'Matériaux BTP',
          description: 'Approvisionnement en matériaux de construction et équipements',
          features: ['Ciment et béton', 'Matériaux de construction', 'Équipements de chantier', 'Livraison sur site'],
          icon: Building2
        },
        {
          title: 'Logistique Chantier',
          description: 'Coordination logistique complète pour vos projets de construction',
          features: ['Planification logistique', 'Transport lourd', 'Gestion des flux', 'Coordination multi-sites'],
          icon: Package
        }
      ]
    },
    alimentaires: {
      denrees: [
        {
          title: 'Céréales et Légumineuses',
          description: 'Import-export de céréales et légumineuses de première nécessité',
          features: ['Riz', 'Blé', 'Maïs', 'Légumineuses'],
          icon: Wheat
        },
        {
          title: 'Huiles et Sucre',
          description: 'Commercialisation d\'huiles végétales et sucre',
          features: ['Huiles végétales', 'Sucre raffiné', 'Produits laitiers', 'Conserves'],
          icon: Droplets
        }
      ],
      halieutiques: [
        {
          title: 'Poissons Frais',
          description: 'Import-export de produits de la pêche fraîche et congelée',
          features: ['Poissons frais', 'Poissons congelés', 'Chaîne du froid', 'Traçabilité'],
          icon: Fish
        },
        {
          title: 'Fruits de Mer',
          description: 'Commercialisation de crustacés et fruits de mer',
          features: ['Crustacés', 'Mollusques', 'Conditionnement', 'Export international'],
          icon: Fish
        }
      ]
    }
  };

  const benefits = [
    {
      icon: Clock,
      title: 'Rapidité',
      description: 'Délais d\'exécution optimisés pour tous nos services'
    },
    {
      icon: Shield,
      title: 'Sécurité',
      description: 'Protection maximale de vos biens et informations'
    },
    {
      icon: Globe,
      title: 'Couverture Internationale',
      description: 'Réseau mondial pour vos opérations internationales'
    },
    {
      icon: Users,
      title: 'Expertise',
      description: 'Équipe qualifiée avec plus de 13 ans d\'expérience'
    }
  ];

  const renderServices = () => {
    const currentServices = services[activeTab];
    
    return (
      <div className="space-y-8">
        {Object.entries(currentServices).map(([subcategoryKey, serviceList]) => {
          const isExpanded = expandedCategories[subcategoryKey];
          const subcategoryTitle = 
            subcategoryKey === 'transport' ? 'Transport' :
            subcategoryKey === 'douanes' ? 'Douanes' :
            subcategoryKey === 'stockage' ? 'Stockage' :
            subcategoryKey === 'petrole' ? 'Hydrocarbures' :
            subcategoryKey === 'btp' ? 'BTP & Travaux Publics' :
            subcategoryKey === 'denrees' ? 'Denrées Première Nécessité' :
            subcategoryKey === 'halieutiques' ? 'Produits Halieutiques' : subcategoryKey;

          return (
            <div key={subcategoryKey} className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleCategory(subcategoryKey)}
                className="w-full px-6 py-4 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                id={subcategoryKey}
              >
                <h3 className="text-xl font-semibold text-gray-900">{subcategoryTitle}</h3>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="p-6 bg-white"
                >
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {serviceList.map((service, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                      >
                        <div className="bg-[#6bb8b1] w-14 h-14 rounded-full flex items-center justify-center mb-4">
                          <service.icon className="w-7 h-7 text-[#3a7c76]" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">
                          {service.title}
                        </h4>
                        <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                          {service.description}
                        </p>
                        <ul className="space-y-1.5">
                          {service.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-center gap-2 text-sm text-gray-600">
                              <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
    <Helmet>
        <title>Services Internationaux - LST OIL SA | Hydrocarbures, Logistique & Produits Alimentaires</title>
        <meta name="description" content="Services complets LST OIL SA : logistique internationale, hydrocarbures, produits alimentaires. Solutions mondiales transport multimodal, douanes, stockage et conseil." />
        <meta name="keywords" content="LST OIL SA, services internationaux, logistique mondiale, transport maritime, fret aérien, hydrocarbures, produits alimentaires, douanes internationales, stockage mondial" />

        <meta property="og:title" content="Services Internationaux - LST OIL SA | Leader National" />
        <meta property="og:description" content="Solutions complètes en logistique internationale, hydrocarbures et produits alimentaires pour marchés mondiaux" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.lstoilsa.com/services" />
        <meta property="og:image" content="https://www.lstoilsa.com/images/services-og.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Services Internationaux LST OIL SA" />
        <meta name="twitter:description" content="Leader mondial en logistique, hydrocarbures et produits alimentaires" />

        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-16 md:py-20 bg-gradient-to-br from-[#6BB8B1] via-[#4B9D96] to-[#3A7C76] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-8 md:mb-6 font-heading">
              Nos Services
            </h1>
            <p className="text-lg md:text-2xl text-white max-w-4xl mx-auto leading-relaxed">
              Solutions complètes et innovantes pour tous vos besoins en logistique, 
              transport et services associés
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Categories Tabs */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          {/* Anchors for hash navigation - IMPORTANT: Ces IDs doivent correspondre exactement aux liens du header */}
          <div id="logistique" className="pt-24 -mt-24" />
          <div id="transport" className="pt-24 -mt-24" />
          <div id="stockage" className="pt-24 -mt-24" />
          <div id="douanes" className="pt-24 -mt-24" />
          <div id="bases" className="pt-24 -mt-24" />
          <div id="petrole" className="pt-24 -mt-24" />
          <div id="btp" className="pt-24 -mt-24" />
          <div id="alimentaires" className="pt-24 -mt-24" />
          <div id="denrees" className="pt-24 -mt-24" />
          <div id="halieutiques" className="pt-24 -mt-24" />
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10 md:mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-heading">
              Nos Domaines d'Expertise
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Découvrez notre gamme complète de services spécialisés
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {serviceCategories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => {
                  setActiveTab(category.id);
                  // Fermer toutes les catégories expandées quand on change d'onglet
                  setExpandedCategories({});
                }}
                className={`flex items-center gap-3 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeTab === category.id
                    ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <category.icon className="w-5 h-5" />
                {category.title}
              </motion.button>
            ))}
          </div>

          {/* Services Content with Dropdowns */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {renderServices()}
          </motion.div>
        </div>
      </section>

      {/* Other Services Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10 md:mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 font-heading">Services Complémentaires</h2>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed">Des offres supplémentaires pour répondre à tous vos besoins</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              id="conseil"
              className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 scroll-mt-24"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="bg-[#6bb8b1] w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-[#3a7c76]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Conseil Logistique</h3>
              <p className="text-gray-600 mb-4">Expertise en optimisation logistique et supply chain management.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Audit logistique</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Optimisation des processus</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Réduction des coûts</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Planification stratégique</li>
              </ul>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-xl shadow-lg border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-[#6bb8b1] w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-[#3a7c76]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Formation et Accompagnement</h3>
              <p className="text-gray-600 mb-4">Formation de vos équipes et accompagnement dans vos projets.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Formation logistique</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Accompagnement projet</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Transfert de compétences</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Support continu</li>
              </ul>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-xl shadow-lg border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="bg-[#6bb8b1] w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <BarChart3 className="w-8 h-8 text-[#3a7c76]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Études et Analyses</h3>
              <p className="text-gray-600 mb-4">Études de faisabilité et analyses de performance logistique.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Études de marché</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Analyse des coûts</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Benchmarking</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Recommandations</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-heading">
              Pourquoi Choisir LST OIL SA ?
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Des avantages concrets qui font la différence
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-[#6bb8b1] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-10 h-10 text-[#3a7c76]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#6BB8B1] to-[#3A7C76] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 font-heading">
              Prêt à Optimiser Votre Logistique ?
            </h2>
            <p className="text-lg md:text-xl text-white mb-8 max-w-3xl mx-auto leading-relaxed">
              Contactez-nous pour un devis personnalisé et découvrez comment 
              nous pouvons transformer vos opérations logistiques
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
                to="/contact"
                className="bg-[#cd3127] hover:bg-[#cd3127]/90 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg inline-flex items-center gap-2"
              >
                Demander un Devis
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="tel:+221338028484"
                className="border-2 border-white text-white hover:bg-white hover:text-[#4b9d96] font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Nous Appeler
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <MapPin className="w-10 h-10 md:w-12 md:h-12 text-[#4b9d96] mx-auto mb-4" />
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">Adresse</h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                Liberté 6 Extension<br />
                villa n°27<br />
                Dakar, Sénégal
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Phone className="w-10 h-10 md:w-12 md:h-12 text-[#4b9d96] mx-auto mb-4" />
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">Téléphone</h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                +221 33 802 84 84<br />
                +221 77 557 77 48
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Mail className="w-10 h-10 md:w-12 md:h-12 text-[#4b9d96] mx-auto mb-4" />
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">Email</h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                lst@lstoil.com<br />
                lstlamine@gmail.com
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
